/**
 * City metadata builder utilities for creating consistent city metadata structures
 * across Lists and Itineraries.
 * 
 * Based on backend confirmation:
 * - Lists: Backend extracts city_identifying_names from postLocations (no builder needed)
 * - Itineraries: Backend accepts city_identifying_names directly and saves user_defined_destinations as-is
 */

import { CityFormData } from '@/types/city';
import { getCityCoordinates } from '@/utils/cityCoordinates';
import { debug } from '@/utils/logger';
import { extractShortFormatAddress } from '@/utils/cityUtils';

/**
 * Structure for Itinerary user_defined_destinations (mobile app format).
 * Matches the structure used by the mobile app for consistency.
 */
export interface ItineraryUserDefinedDestination {
  /** Full identifying name (e.g., "Atlanta, Georgia, United States") */
  name: string;
  /** City name only (e.g., "Atlanta") */
  label: string;
  /** Location ID (optional) */
  location_id?: string;
  /** Alternative ID field (optional) */
  id?: string;
  /** Google Place ID (optional) */
  google_place_id?: string;
  /** GeoJSON format geoposition (MongoDB format: [longitude, latitude]) */
  geoposition?: {
    type: 'Point';
    coordinates: [number, number];
  };
  /** Formatted address (e.g., "Atlanta, GA, USA") */
  a3_intl_address?: string;
}

/**
 * Result structure for building Itinerary city metadata.
 */
export interface ItineraryCityMetadata {
  /** City identifying names array (for backend) */
  city_identifying_names: string[];
  /** User defined destinations array (mobile app structure) */
  user_defined_destinations: ItineraryUserDefinedDestination[];
}

/**
 * Ensures city_identifying_name is complete (includes city name).
 * Handles cases where mobile app may have incomplete identifying names.
 * 
 * @param cityName - City name (e.g., "Atlanta")
 * @param identifyingName - City identifying name (e.g., "Georgia, United States" or "Atlanta, Georgia, United States")
 * @returns Complete identifying name with city name included
 * 
 * @example
 * ```typescript
 * ensureCompleteIdentifyingName("Atlanta", "Georgia, United States")
 * // Returns: "Atlanta, Georgia, United States"
 * 
 * ensureCompleteIdentifyingName("Atlanta", "Atlanta, Georgia, United States")
 * // Returns: "Atlanta, Georgia, United States" (already complete)
 * ```
 */
export function ensureCompleteIdentifyingName(
  cityName: string,
  identifyingName: string
): string {
  if (!cityName || !identifyingName) {
    return identifyingName || cityName || '';
  }

  const cityNameLower = cityName.toLowerCase().trim();
  const identifyingNameLower = identifyingName.toLowerCase().trim();

  // If identifying_name already starts with city name, return as-is
  if (identifyingNameLower.startsWith(cityNameLower)) {
    return identifyingName;
  }

  // If identifying_name doesn't start with city name, prepend it
  // This handles mobile app cases like "Georgia, United States" -> "Atlanta, Georgia, United States"
  return `${cityName}, ${identifyingName}`;
}

/**
 * Builds city metadata for Itineraries (uses itinerary_data.user_defined_destinations structure).
 * Matches mobile app structure exactly.
 * 
 * Backend accepts city_identifying_names directly in post.city_identifying_names.
 * Backend does NOT extract it from user_defined_destinations.
 * 
 * @param city - City form data (can be null)
 * @returns City metadata structure for Itineraries
 * 
 * @example
 * ```typescript
 * buildItineraryCityMetadata({
 *   city: "Atlanta",
 *   city_identifying_name: "Georgia, United States",
 *   google_place_id: "ChIJjQ-I5nuBJ4gR4rRPyYQyY1E",
 *   geo_position: { lat: 33.749, long: -84.388 }
 * })
 * // Returns: {
 * //   city_identifying_names: ["Atlanta, Georgia, United States"],
 * //   user_defined_destinations: [{
 * //     name: "Atlanta, Georgia, United States",
 * //     label: "Atlanta",
 * //     geoposition: { type: "Point", coordinates: [-84.388, 33.749] },
 * //     ...
 * //   }]
 * // }
 * ```
 */
export function buildItineraryCityMetadata(
  city: CityFormData | null
): ItineraryCityMetadata {
  if (!city) {
    debug('[buildItineraryCityMetadata] No city provided, returning empty metadata');
    return {
      city_identifying_names: [],
      user_defined_destinations: [],
    };
  }

  // Ensure city_identifying_name is complete
  const cityIdentifyingName = city.city_identifying_name || city.a3_intl_address || '';
  const cityName = city.city || city.name || '';

  debug('[buildItineraryCityMetadata] Building metadata from city', {
    cityName,
    city_identifying_name: city.city_identifying_name,
    a3_intl_address: city.a3_intl_address,
    hasCityIdentifyingName: !!city.city_identifying_name,
    hasA3IntlAddress: !!city.a3_intl_address,
    cityIdentifyingName,
  });

  const completeIdentifyingName = ensureCompleteIdentifyingName(
    cityName,
    cityIdentifyingName
  );

  if (!completeIdentifyingName) {
    debug('[buildItineraryCityMetadata] No identifying name found, returning empty metadata', {
      cityName,
      cityIdentifyingName,
      completeIdentifyingName,
      cityKeys: Object.keys(city),
    });
    return {
      city_identifying_names: [],
      user_defined_destinations: [],
    };
  }

  // Extract coordinates
  const coords = getCityCoordinates(city);

  // Mobile app structure: name = full identifying name, label = city name only
  // Coordinates in MongoDB GeoJSON format: [longitude, latitude]
  
  // Extract short format address from google_places_data if a3_intl_address not available
  const googlePlacesData = city.google_places_data as Record<string, unknown> | undefined;
  const shortFormatAddress = city.a3_intl_address || extractShortFormatAddress(googlePlacesData, completeIdentifyingName);
  
  // Fallback to completeIdentifyingName if no short format found
  const finalA3IntlAddress = shortFormatAddress || completeIdentifyingName;
  
  debug('[buildItineraryCityMetadata] Building user_defined_destination', {
    city_a3_intl_address: city.a3_intl_address,
    hasGooglePlacesData: !!city.google_places_data,
    shortFormatAddress,
    finalA3IntlAddress,
    completeIdentifyingName,
    isShortFormat: /,\s*[A-Z]{2}\s*,/.test(finalA3IntlAddress),
  });
  
  const userDefinedDestination: ItineraryUserDefinedDestination = {
    name: completeIdentifyingName, // Full identifying name
    label: cityName || completeIdentifyingName.split(',')[0]?.trim() || '', // City name only
    location_id: city._id,
    id: city._id,
    google_place_id: city.google_place_id,
    a3_intl_address: finalA3IntlAddress, // Use extracted short format or fallback
  };

  // Add geoposition if coordinates are available
  if (coords) {
    userDefinedDestination.geoposition = {
      type: 'Point' as const,
      coordinates: [coords.lng, coords.lat] as [number, number], // [longitude, latitude]
    };
  }

  debug('[buildItineraryCityMetadata] Built metadata', {
    city_identifying_names: [completeIdentifyingName],
    user_defined_destination: {
      name: userDefinedDestination.name,
      label: userDefinedDestination.label,
      has_geoposition: !!userDefinedDestination.geoposition,
    },
  });

  return {
    city_identifying_names: [completeIdentifyingName],
    user_defined_destinations: [userDefinedDestination],
  };
}
